import React from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import { Box, Grid, Paper, Typography } from '@mui/material'
import { Skeleton } from '@mui/material'
import Joi from 'joi'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { CardTitle } from 'components/common/controls'
import SmsFormControls from 'components/settings/CustomerSms/SmsFormControls'
import SmsTokens from 'components/settings/CustomerSms/SmsTokens'
import {
  SmsTemplateType,
  GetSmsTemplatesQuery,
  UpdateSmsTemplateInput,
  useGetSmsTemplatesQuery,
  useGetSmsTemplateTokensQuery,
  useUpdateSmsTemplateMutation,
} from 'generated/graphql'

const smsFormSchema = Joi.object({
  message: Joi.string().required().max(160),
})

interface IProps {
  type: SmsTemplateType
  label: string
}

export const SmsTemplate = (props: IProps) => {
  const { type, label } = props
  const [smsTemplate, setSmsTemplate] =
    React.useState<GetSmsTemplatesQuery['smsTemplates'][0]>()

  const spacing = 3

  const methods = useForm<UpdateSmsTemplateInput>({
    shouldUnregister: true,
    resolver: joiResolver(smsFormSchema),
    defaultValues: {
      type,
      message: '',
    },
  })
  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = methods

  // Queries
  const { loading: smsTemplatesLoading } = useGetSmsTemplatesQuery({
    variables: {
      where: { type: { eq: type } },
    },
    onCompleted: (response) => {
      setSmsTemplate(response.smsTemplates[0])
    },
  })

  const { data: smsTemplateTokens, loading: smsTemplateTokensLoading } =
    useGetSmsTemplateTokensQuery()

  // Mutation
  const [updateSmsTemplate, { loading: updatingSmsTemplate }] = useUpdateSmsTemplateMutation({
    onCompleted: (response) => {
      if (!response) {
        toast.error('Something went wrong')
        return
      }

      toast.success(`SMS Template was successfully updated`)
    },
  })

  React.useEffect(() => {
    reset({
      ...smsTemplate,
    })
  }, [reset, smsTemplate])

  const smsTemplateTokensFiltered = React.useMemo(
    () =>
      smsTemplateTokens?.smsTemplateTokens.filter((token) =>
        smsTemplate?.tokens.includes(token.value),
      ),
    [smsTemplate, smsTemplateTokens],
  )

  const onSubmit = (formData: UpdateSmsTemplateInput) => {
    updateSmsTemplate({
      variables: {
        input: {
          id: smsTemplate?.id,
          type,
          message: formData.message,
        },
      },
    })
  }

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={spacing}>
          <Grid item xs={12} md={8}>
            <Paper variant='outlined' sx={{ padding: 2 }}>
              <CardTitle>{`${label} Details`}</CardTitle>
              {smsTemplatesLoading ? (
                <>
                  <Typography variant='h2'>
                    <Skeleton animation='wave' />
                    <Skeleton animation='wave' />
                  </Typography>
                  <Typography variant='h1'>
                    <Skeleton animation='wave' />
                  </Typography>
                </>
              ) : (
                <Box py={1}>
                  <SmsFormControls />
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant='outlined' sx={{ padding: 2, height: '100%' }}>
              <CardTitle>SMS Tokens</CardTitle>
              {smsTemplateTokensLoading ? (
                <>
                  <Box py={0.5}>
                    <Typography variant='h3'>
                      <Skeleton animation='wave' />
                      <Skeleton animation='wave' />
                    </Typography>
                  </Box>
                </>
              ) : (
                <SmsTokens tokens={smsTemplateTokensFiltered} />
              )}
            </Paper>
          </Grid>

          <Grid item container justifyContent='flex-end'>
            <Grid item>
              <LoadingButton
                variant='contained'
                color='primary'
                type='submit'
                loading={updatingSmsTemplate}
                disabled={!isDirty}>
                Save
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
